import { useCallback, useEffect, useRef, useState } from 'react'
import { useSettings } from 'core/hooks/settings/useSettings'
import Drawer from 'core/components/drawer/Drawer'
import { TripDetailRef } from 'core/widgets/trip-detail/TripDetail'
import { useQueryClient } from '@tanstack/react-query'
import UnsavedModal from 'core/components/dialogs/UnsavedModal'
import useUrlParams from 'core/hooks/navigation/useUrlParams'
import useNavigation from 'core/hooks/navigation/useNavigation'
import { TripsUrl } from 'constants/RouteUrls'
import TripDetail from 'core/widgets/trip-detail/TripDetailLazy'

const TripDetailDrawer = () => {
  const { set, get } = useSettings()
  const { tokenId, view, props } = get('tripDetail') ?? {}
  const queryClient = useQueryClient()
  const tripDetailRef = useRef<TripDetailRef>(null)
  const [showUnsavedModal, setShowUnsavedModal] = useState(false)
  const [urlParams, setUrlParams] = useUrlParams()
  const { pathname } = useNavigation()
  const handleClose = useCallback(() => {
    setShowUnsavedModal(false)
    set('tripDetail', {
      tokenId: '',
      props: {}
    })

    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ['trip', tokenId] })
    }, 500)
  }, [queryClient, set, tokenId])

  const handleBeforeClose = useCallback(() => {
    if (!tripDetailRef?.current?.getFormDirty()) {
      return handleClose()
    }

    setShowUnsavedModal(true)
  }, [handleClose])

  useEffect(() => {
    if (pathname !== TripsUrl) return

    const selectedParam = urlParams.get('t_sr')

    if (tokenId && tokenId !== 'new-trip-id' && !selectedParam) {
      urlParams.set('t_sr', tokenId)
      setUrlParams(urlParams)
    }

    if (!tokenId && selectedParam) {
      urlParams.delete('t_sr')
      setUrlParams(urlParams)
    }
  }, [pathname, setUrlParams, tokenId, urlParams])

  return (
    <Drawer
      show={!!tokenId}
      startFrom="right"
      onClose={handleBeforeClose}
      width="w-full md:w-2/4 lg:w-2/6"
      canExpand={false}
      contentClassNames="overflow-scroll h-screen"
    >
      <TripDetail
        ref={tripDetailRef}
        view={view}
        tripId={tokenId}
        onError={() => {
          props?.onError?.()
        }}
        onSuccess={() => {
          props?.onSuccess?.()
          handleClose()
        }}
        enableEndDate={props?.enabledEndDate ?? null}
        enableStartDate={props?.enabledStartDate ?? null}
      />
      <UnsavedModal
        active={showUnsavedModal}
        overrideShow={showUnsavedModal}
        saveButtonText="Keep editing"
        onExit={() => {
          setShowUnsavedModal(false)
          setTimeout(() => {
            handleClose()
          }, 500)
        }}
        onSave={() => {
          setShowUnsavedModal(false)
        }}
      />
    </Drawer>
  )
}

export default TripDetailDrawer
